import type { RoiId } from '~/domain/changes/ids';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import type { ViewManager } from '~/engine/view/ViewManager';
import { focusViewsOf } from './focusViewsOf';

/**
 * Frames every tile of the image series `seriesUid` on an ROI's contour,
 * each tile in its own plane (see {@link focusViewsOf}). Frames built
 * content only; focusing never builds a texture, so an ROI that is empty,
 * point-only, or hidden and never drawn moves no tile.
 * @returns Whether a tile shows the image: false when the series is not
 *   resident or no tile shows it. True also when the ROI has no built
 *   contour to frame.
 */
export function focusGridRoi(
    registry: DatasetEnvironmentRegistry,
    viewManager: ViewManager | null,
    seriesUid: SeriesInstanceUid | undefined,
    roiId: RoiId
): boolean {
    const target = focusViewsOf(registry, viewManager, seriesUid);
    if (!target) {
        return false;
    }
    const texture =
        target.environment.structureSet?.getRoi(roiId)?.builtTexture;
    if (texture && !texture.isEmpty) {
        target.environment.panZoom.focusViewsTo(
            target.views,
            texture.contentBoundingBox
        );
    }
    return true;
}
